import { useEffect, useMemo, useState } from 'react';
import { Button, Field, Input, Select, todayIn } from '@busbook/ui';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../lib/tenant.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

interface Stop {
  id: string;
  name: string;
  city: string;
}

/**
 * Origin, destination, date, passengers (docs/05). Stops come from the tenant's own network,
 * so the dropdowns can never offer another company's stops.
 *
 * Laid out as a journey rather than as four unrelated fields: origin and destination sit in
 * one bordered group joined by a swap control, because the commonest second search anybody
 * runs is the way back, and re-picking both stops from a list to do it is the kind of small
 * friction people give up on.
 */
export function SearchForm() {
  const t = useT();
  const { settings } = useTenant();
  const navigate = useNavigate();

  const today = todayIn(settings.timezone);
  const [stops, setStops] = useState<Stop[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [date, setDate] = useState(() => today);
  const [passengers, setPassengers] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.request<Stop[]>('/public/stops', { method: 'GET', auth: false });
        if (!cancelled) setStops(result);
      } catch {
        if (!cancelled) setStops([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const destinations = useMemo(
    () => (stops ?? []).filter((stop) => stop.id !== from),
    [stops, from],
  );

  const noNetworkYet = stops !== null && stops.length === 0;
  const tomorrow = addDays(today, 1);

  function swap() {
    // Both at once: setting them in sequence would briefly make origin and destination equal
    // and trip the filter that removes the origin from the destination list.
    setFrom(to);
    setTo(from);
    setError(null);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!from || !to) {
      setError(t('s.errChooseBoth'));
      return;
    }
    if (from === to) {
      setError(t('s.errSameStop'));
      return;
    }
    setError(null);
    const params = new URLSearchParams({ from, to, date, passengers: String(passengers) });
    navigate(`/results?${params.toString()}`);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div
        className="relative rounded-xl border"
        style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      >
        <div className="p-3 pb-2">
          <Field label={t('c.from')} htmlFor="from">
            <Select
              id="from"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              disabled={noNetworkYet}
            >
              <option value="">{t('c.selectStop')}</option>
              {(stops ?? []).map((stop) => (
                <option key={stop.id} value={stop.id}>
                  {stop.name} · {stop.city}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div
          className="mx-3 border-t"
          style={{ borderColor: 'var(--color-line)' }}
          aria-hidden
        />

        <div className="p-3 pt-2">
          <Field label={t('c.to')} htmlFor="to">
            <Select
              id="to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={noNetworkYet}
            >
              <option value="">{t('c.selectStop')}</option>
              {destinations.map((stop) => (
                <option key={stop.id} value={stop.id}>
                  {stop.name} · {stop.city}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Sits ON the divider between the two fields, which is what makes it read as
            "reverse these" rather than as a third control. 44px box for the thumb. */}
        <button
          type="button"
          onClick={swap}
          disabled={!from && !to}
          aria-label={t('s.swap')}
          className="absolute right-3 grid h-11 w-11 place-items-center rounded-full border disabled:opacity-40"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            borderColor: 'var(--color-line)',
            background: 'var(--color-bg)',
            color: 'var(--color-primary)',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M7 4v14M7 4 4 7M7 4l3 3" />
            <path d="M17 20V6M17 20l3-3M17 20l-3-3" />
          </svg>
        </button>
      </div>

      {/*
        Wraps rather than squeezes. A native date input has a large intrinsic minimum -- 170px
        in Chrome, and more with a bigger system font or a longer date format -- which a `1fr`
        grid track cannot shrink below. Beside a fixed 7.5rem column that came to 302px inside
        the 288px a 320px phone has, so the passenger control was pushed off the right edge and
        the page scrolled sideways.

        Flex wrapping fixes it without a breakpoint: the two sit side by side wherever both fit
        and the passenger count drops to its own line where they do not. That keeps working for
        a font size or a translation nobody has measured, which a chosen width would not.
      */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 basis-[11rem]">
        <Field label={t('c.date')} htmlFor="date">
          <Input
            id="date"
            type="date"
            value={date}
            min={today}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        </div>
        <div className="w-[7.5rem] flex-none">
        <Field label={t('c.passengers')} htmlFor="passengers">
          <Select
            id="passengers"
            value={passengers}
            onChange={(e) => setPassengers(Number(e.target.value))}
          >
            {Array.from({ length: settings.maxSeatsPerBooking }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </Field>
        </div>
      </div>

      {/* Almost every search is for today or tomorrow, and a native date input costs three
          taps to say so. These are the two shortcuts, not a replacement for the field. */}
      <div className="-mt-1 flex gap-2">
        <DateChip label={t('s.today')} active={date === today} onClick={() => setDate(today)} />
        <DateChip
          label={t('s.tomorrow')}
          active={date === tomorrow}
          onClick={() => setDate(tomorrow)}
        />
      </div>

      {error && (
        <p className="text-sm" style={{ color: 'var(--signal-cancelled)' }} role="alert">
          {error}
        </p>
      )}

      {noNetworkYet ? (
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('c.noRoutes')}
        </p>
      ) : (
        <Button type="submit" size="lg" block disabled={stops === null}>
          {t('c.searchDepartures')}
        </Button>
      )}
    </form>
  );
}

function DateChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="rounded-full border px-3 py-1.5 text-sm font-medium"
      style={{
        borderColor: active ? 'var(--color-primary)' : 'var(--color-line)',
        background: active ? 'var(--color-primary-wash)' : 'transparent',
        color: active ? 'var(--color-primary)' : 'var(--color-muted)',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Calendar arithmetic on the YYYY-MM-DD string the input speaks, in UTC.
 *
 * Deliberately not `new Date()` plus a day: that would add 24 hours to an instant, which is
 * not the same as the next calendar day across a DST boundary, and the operator's timezone
 * has already decided what "today" means before this runs.
 */
function addDays(isoDate: string, days: number): string {
  const at = new Date(`${isoDate}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

export { addDays };
